// src/components/ExpressionIndicator.tsx
/**
 * ExpressionIndicator Component (T-17)
 * 
 * Visual marker on the number line showing the position of a mathematical expression.
 * Features KaTeX rendering, selection states, and click-to-edit functionality.
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectExpressionObject, showExpressionWidget, deleteExpressionObject, toggleExpressionSelection } from '../store/slices/expressionsSlice';
import { ExpressionObject } from '../store/slices/expressionsSlice';
import { formatNumber, formatNumberWithPrecision, LocaleCode } from '../lib/locale';
import { selectLocale, selectTextScale } from '../store/slices/preferencesSlice';
import MathRenderer from './MathRenderer';

interface ExpressionIndicatorProps {
  /** The expression object to display */
  object: ExpressionObject;
  /** X coordinate on the number line (pixels) */
  x: number;
  /** Y coordinate on the number line (pixels) */
  y: number;
  /** Whether this is a horizontal or vertical number line */
  isHorizontal: boolean;
  /** Whether the object is currently selected */
  isSelected: boolean;
  /** Callback when the object is clicked */
  onSelect?: (id: string) => void;
  /** Callback when the object should be edited */
  onEdit?: (id: string) => void;
  /** Callback when the object should be deleted */
  onDelete?: (id: string) => void;
}

/**
 * ExpressionIndicator renders a visual marker for an expression object on the number line
 */
export default function ExpressionIndicator({
  object,
  x,
  y,
  isHorizontal,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}: ExpressionIndicatorProps) {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const textScale = useSelector(selectTextScale);
  const [showValue, setShowValue] = useState(false);

  // Handle click events
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect(object.id);
    } else {
      // Use toggleExpressionSelection for multi-selection support with Ctrl key
      dispatch(toggleExpressionSelection({ 
        id: object.id, 
        ctrlKey: event.ctrlKey || event.metaKey 
      }));
    }
    // Toggle value visibility on click
    setShowValue(!showValue);
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onEdit) {
      onEdit(object.id);
    } else {
      dispatch(showExpressionWidget({
        position: { x: event.clientX, y: event.clientY },
        mode: 'edit',
        expression: object.expression,
        objectId: object.id,
      }));
    }
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // For now, just show edit widget on right-click
    // Could be extended to show a context menu in the future
    if (onEdit) {
      onEdit(object.id);
    } else {
      dispatch(showExpressionWidget({
        position: { x: event.clientX, y: event.clientY },
        mode: 'edit',
        expression: object.expression,
        objectId: object.id,
      }));
    }
  };

  // Calculate marker dimensions based on size setting
  const getMarkerSize = () => {
    switch (object.style.size) {
      case 'small': return 8;
      case 'large': return 16;
      default: return 12; // medium
    }
  };

  // Calculate label offset from marker
  const getLabelOffset = () => {
    switch (object.style.size) {
      case 'small': return 30;
      case 'large': return 40;
      default: return 35; // medium
    }
  };

  const markerSize = getMarkerSize();
  const labelOffset = getLabelOffset();

  // Position calculations for marker and label
  const markerX = x;
  const markerY = y;
  
  // Label positioning depends on orientation and available space
  // Horizontal: label is centered above the marker
  // Vertical: label is to the right of the marker, vertically centered
  const labelX = isHorizontal ? x : x + (markerSize + 10);
  const labelY = isHorizontal ? y - labelOffset : y;

  // Colors based on selection state and style
  const markerColor = isSelected ? '#3B82F6' : object.style.color; // Blue when selected
  const strokeColor = isSelected ? '#1D4ED8' : darkenColor(object.style.color, 0.2);
  const labelBgColor = isSelected ? '#EFF6FF' : '#FFFFFF';
  const labelBorderColor = isSelected ? '#3B82F6' : '#E5E7EB';

  // Don't render if position is invalid
  if (object.position === null) {
    return null;
  }

  return (
    <g className="expression-indicator" style={{ opacity: object.style.opacity }}>
      {/* Marker circle */}
      <circle
        cx={markerX}
        cy={markerY}
        r={markerSize}
        fill={markerColor}
        stroke={strokeColor}
        strokeWidth={isSelected ? 3 : 2}
        className="expression-marker"
        style={{ 
          cursor: 'pointer',
          filter: isSelected ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))' : 'none',
          transform: 'translate(0, 0)' // Prevent any transform changes
        }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      />

      {/* Label with expression */}
      {object.style.showLabel && (
        <g className="expression-label-container">
          {/* Background rectangle */}
          <rect
            x={isHorizontal ? labelX - 50 : labelX}
            y={isHorizontal ? labelY - 16 : labelY - 12}
            width={100}
            height={24}
            fill={labelBgColor}
            stroke={labelBorderColor}
            strokeWidth="1"
            rx="4"
            className="expression-label-bg"
          />
          {/* Expression text */}
          <foreignObject
            x={isHorizontal ? labelX - 50 : labelX}
            y={isHorizontal ? labelY - 16 : labelY - 12}
            width={100}
            height={24}
            className="expression-label"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontSize: `${(object.style.size === 'small' ? 11 : object.style.size === 'large' ? 15 : 13) * textScale}px`,
                fontWeight: isSelected ? '600' : '500',
                cursor: 'pointer',
                color: isSelected ? '#1D4ED8' : '#374151',
              }}
              onClick={handleClick}
              onDoubleClick={handleDoubleClick}
              onContextMenu={handleContextMenu}
            >
              <MathRenderer 
                expression={object.expression}
                style={{ 
                  color: 'inherit',
                  fontWeight: 'inherit'
                }}
              />
            </div>
          </foreignObject>
        </g>
      )}

      {/* Value display (shown only when clicked) */}
      {showValue && object.position !== null && (
        <foreignObject
          x={isHorizontal ? labelX - 40 : labelX}
          y={isHorizontal ? labelY + (object.style.showLabel ? 20 : -12) : labelY + (object.style.showLabel ? 16 : 0)}
          width={80}
          height={16}
          className="expression-value-container"
        >
          <div
            style={{
              backgroundColor: '#F3F4F6',
              border: '1px solid #D1D5DB',
              borderRadius: '3px',
              padding: '1px 4px',
              fontSize: `${10 * textScale}px`,
              fontFamily: 'monospace',
              textAlign: 'center',
              color: '#6B7280',
            }}
          >
            {formatNumberWithPrecision(object.position, locale, 3)}
          </div>
        </foreignObject>
      )}

      {/* Variable dependency indicators removed - not needed for expression objects */}
    </g>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Darken a hex color by a given amount (0-1)
 */
function darkenColor(color: string, amount: number): string {
  // Simple darkening - in a real app you might use a color library
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));
    
    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  }
  return color;
}

